'use client';
import { createContext, useCallback, useContext, useEffect, type ReactNode } from 'react';

import { usePersistentState } from './usePersistentState';

/** Whole-app UI scale (CSS `zoom` on the document root) — a personal PREFERENCE and nothing else: a
 *  per-device localStorage value saying how big the user likes things, because the right size depends on
 *  the eyes and the screen in front of you. It is off (1) until someone moves the Account slider.
 *
 *  There is deliberately no automatic width-derived component. One used to multiply in underneath this
 *  one, and it was unsound rather than merely mistuned: it exempted phones and shrank everything else,
 *  which makes it discontinuous by construction — 767px rendered at 100% and 768px at 70%, flipping the
 *  app from a phone layout at full size to a desktop layout at 70% on one pixel of window travel. It was
 *  also non-monotonic below the reference width (a 1366px window got a WIDER layout viewport than a
 *  1440px one), it parked every tablet and every 1280/1366 laptop on its 0.7 floor, and by inflating the
 *  layout viewport it made `@media` and `@container` queries fire against a viewport no window ever had
 *  — so the mobile rules never reached a tablet. Fitting the design to the window is the stylesheet's
 *  job, and it is done there now. */

const KEY = 'elowen:ui-scale';
export const MIN_SCALE = 0.8;
export const MAX_SCALE = 1.5;
export const DEFAULT_SCALE = 1;

const clamp = (n: number): number => Math.min(MAX_SCALE, Math.max(MIN_SCALE, n));

const isFiniteScale = (value: string) => Number.isFinite(Number(value));

interface UiScaleValue {
  /** The user's personal factor — what the Account slider sets. */
  preference: number;
  setPreference: (n: number) => void;
}
const UiScaleContext = createContext<UiScaleValue | null>(null);

export function UiScaleProvider({ children }: { children: ReactNode }) {
  const [stored, setStored] = usePersistentState(KEY, String(DEFAULT_SCALE), isFiniteScale);
  const preference = clamp(Number(stored));

  // Keep the document root's zoom in lockstep with the preference. The `--ui-scale` var is published too
  // so viewport-height layout (e.g. the shell's full-height column) can divide by it: a `100dvh` box
  // under `zoom: z` renders at z×viewport, so full-height containers must size to `100dvh / z` to fill.
  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty('zoom', String(preference));
    root.setProperty('--ui-scale', String(preference));
  }, [preference]);

  const setPreference = useCallback((n: number) => {
    const c = clamp(n);
    setStored(String(c));
  }, [setStored]);

  return <UiScaleContext.Provider value={{ preference, setPreference }}>{children}</UiScaleContext.Provider>;
}

export function useUiScale(): UiScaleValue {
  const ctx = useContext(UiScaleContext);
  if (!ctx) throw new Error('useUiScale must be used within UiScaleProvider');
  return ctx;
}
